import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Check } from 'lucide-react';
import { clsx } from '../../ds/clsx';
import { Avatar, Banner, Button, EmptyState, Eyebrow, Icon, Skeleton, useToast } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { ControllingStrip } from '../controlling/ControllingStrip';
import { SetupBanner } from '../onboarding/user/SetupBanner';
import {
  bookAppointmentHref,
  isNextStepRequired,
  useCompleteTask,
  useReassignTask,
  useSnoozeTask,
  useTasks,
  type ReassignMode,
  type TaskItem,
  type TasksResponse,
} from './api';
import { CreateTaskDialog } from './CreateTaskDialog';
import { dueSentence, longDay, todayKey } from './dates';
import { NextStepDialog, type NextStep } from './NextStepDialog';
import { SnoozePopover, type SnoozeValue } from './SnoozePopover';
import { ReassignDialog, StuckMenu } from './StuckActions';
import { TaskDetailSheet } from './TaskDetailSheet';
import styles from './TasksPage.module.css';

type BlockKey = 'stuck' | 'overdue' | 'today' | 'later';

interface Ghost {
  task: TaskItem;
  block: BlockKey;
  index: number;
}

const DONE_ROW_MS = 1000;

const PRIORITY_CLASS: Record<TaskItem['priority'], string | undefined> = {
  a: styles.prioA,
  b: styles.prioB,
  c: styles.prioC,
  d: styles.prioD,
};

/** „Heute haben Sie 19 Tasks geschlossen — 11 Anrufe, 5 Nachrichten, 3 Freigaben." */
function doneSentence(header: TasksResponse['header']): string {
  if (header.doneToday === 0) return 'Heute haben Sie noch keine Tasks geschlossen.';
  const groups = header.doneTodayByGroup;
  const parts: string[] = [];
  if (groups.calls > 0) parts.push(groups.calls === 1 ? '1 Anruf' : `${groups.calls} Anrufe`);
  if (groups.messages > 0)
    parts.push(groups.messages === 1 ? '1 Nachricht' : `${groups.messages} Nachrichten`);
  if (groups.approvals > 0)
    parts.push(groups.approvals === 1 ? '1 Freigabe' : `${groups.approvals} Freigaben`);
  if (groups.other > 0) parts.push(groups.other === 1 ? '1 weitere' : `${groups.other} weitere`);
  const count = header.doneToday === 1 ? '1 Task' : `${header.doneToday} Tasks`;
  return parts.length > 0
    ? `Heute haben Sie ${count} geschlossen — ${parts.join(', ')}.`
    : `Heute haben Sie ${count} geschlossen.`;
}

function nextDueSentence(header: TasksResponse['header'], today: string): string {
  if (header.nextDue === null) return 'Es ist keine weitere Task geplant.';
  return `Die nächste Task wird ${dueSentence(header.nextDue.on, header.nextDue.time, today)} fällig.`;
}

/** Whether the row's CTA opens the task itself (no linked record) rather than another module. */
function opensDetail(task: TaskItem): boolean {
  return task.href.startsWith('/tasks?task=');
}

/**
 * `/tasks` — „Meine Tasks" (Task-Queue 01–05; Backoffice 01/02 for the stuck-task assignee; Finanzierer
 * 01/02 for financiers) with the controlling strip (Controlling 01–03) above the blocks. `?task=<id>`
 * highlights a row and opens its action, `?later=1` expands „Später", `/tasks/new` opens the editor.
 */
export function TasksPage({ create = false }: { create?: boolean | undefined }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const workspace = useWorkspace();
  const mobile = useIsMobile();
  const { toast } = useToast();
  const later = params.get('later') === '1';
  const highlightId = params.get('task');
  const tasks = useTasks(later);
  const snooze = useSnoozeTask();
  const complete = useCompleteTask();
  const reassign = useReassignTask();
  const today = todayKey();

  const [snoozeId, setSnoozeId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [reassignState, setReassignState] = useState<{
    task: TaskItem;
    mode: Exclude<ReassignMode, 'take_over'>;
  } | null>(null);
  const [nextStepTask, setNextStepTask] = useState<TaskItem | null>(null);
  const [nextStepError, setNextStepError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [ghosts, setGhosts] = useState<Ghost[]>([]);
  const snoozeTriggers = useRef(new Map<string, HTMLButtonElement | null>());
  const menuTriggers = useRef(new Map<string, HTMLButtonElement | null>());
  const highlighted = useRef<string | null>(null);

  const financier = workspace.user.roleKey === 'financier';
  const data = tasks.data;

  // ?task=<id>: scroll to the row; a task without a record opens its detail.
  useEffect(() => {
    if (!data || highlightId === null || highlighted.current === highlightId) return;
    const all = [
      ...(data.blocks.stuck ?? []),
      ...data.blocks.overdue,
      ...data.blocks.today,
      ...(data.blocks.later ?? []),
    ];
    const task = all.find((entry) => entry.id === highlightId);
    if (!task) return;
    highlighted.current = highlightId;
    document.getElementById(`task-${task.id}`)?.scrollIntoView({ block: 'center' });
    if (opensDetail(task) || mobile) setDetailId(task.id);
  }, [data, highlightId, mobile]);

  const setLater = useCallback(
    (expanded: boolean) => {
      const next = new URLSearchParams(params);
      if (expanded) next.set('later', '1');
      else next.delete('later');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const openTask = useCallback(
    (task: TaskItem) => {
      if (task.type === 'appointment_outcome' && task.appointmentId !== null) {
        void navigate(`/calendar/appointments/${task.appointmentId}?outcome=1`);
        return;
      }
      if (opensDetail(task)) {
        setDetailId(task.id);
        return;
      }
      void navigate(task.href);
    },
    [navigate],
  );

  const markDone = useCallback(
    (task: TaskItem) => {
      if (!data) return;
      const blocks: Array<[BlockKey, TaskItem[]]> = [
        ['stuck', data.blocks.stuck ?? []],
        ['overdue', data.blocks.overdue],
        ['today', data.blocks.today],
        ['later', data.blocks.later ?? []],
      ];
      for (const [block, rows] of blocks) {
        const index = rows.findIndex((row) => row.id === task.id);
        if (index >= 0) {
          setGhosts((current) => [...current, { task, block, index }]);
          window.setTimeout(() => {
            setGhosts((current) => current.filter((ghost) => ghost.task.id !== task.id));
          }, DONE_ROW_MS);
          return;
        }
      }
    },
    [data],
  );

  const completeTask = useCallback(
    async (task: TaskItem, nextStep?: NextStep) => {
      try {
        const result = await complete.mutateAsync({ id: task.id, body: nextStep ? { nextStep } : {} });
        setNextStepTask(null);
        setNextStepError(null);
        setDetailId(null);
        markDone(task);
        if (result.next === 'book_appointment' && task.contactId !== null) {
          void navigate(bookAppointmentHref(task.contactId, result.createdTaskId));
        }
      } catch (error) {
        if (isNextStepRequired(error) && nextStep === undefined) {
          setNextStepError(null);
          setNextStepTask(task);
          return;
        }
        if (nextStep !== undefined) setNextStepError(errorMessage(error));
        else toast({ message: errorMessage(error) });
      }
    },
    [complete, markDone, navigate, toast],
  );

  const snoozeTask = useCallback(
    async (task: TaskItem, value: SnoozeValue) => {
      await snooze.mutateAsync({ id: task.id, body: value });
      setSnoozeId(null);
      toast({
        message: `Verschoben auf ${longDay(value.dueOn)}${value.dueTime ? `, ${value.dueTime}` : ''}`,
      });
    },
    [snooze, toast],
  );

  const runReassign = useCallback(
    async (
      task: TaskItem,
      mode: ReassignMode,
      input: { assigneeId?: string; reason: string | null } = { reason: null },
    ) => {
      const updated = await reassign.mutateAsync({ id: task.id, body: { mode, ...input } });
      setReassignState(null);
      const messages: Record<ReassignMode, string> = {
        take_over: 'Übernommen',
        give_back: `Zurückgegeben an ${updated.assignee.name}`,
        assign: `Zugewiesen an ${updated.assignee.name}`,
        escalate: `Eskaliert an ${updated.assignee.name}`,
      };
      toast({ message: messages[mode] });
    },
    [reassign, toast],
  );

  function onStuckAction(task: TaskItem, mode: ReassignMode) {
    if (mode === 'take_over') {
      runReassign(task, mode).catch((error: unknown) => toast({ message: errorMessage(error) }));
      return;
    }
    reassign.reset();
    setReassignState({ task, mode });
  }

  const quickPicks = data?.quickPicks ?? [];

  function withGhosts(block: BlockKey, rows: TaskItem[]): Array<{ task: TaskItem; done: boolean }> {
    const result = rows.map((task) => ({ task, done: false }));
    for (const ghost of ghosts) {
      if (ghost.block !== block || result.some((entry) => entry.task.id === ghost.task.id)) continue;
      result.splice(Math.min(ghost.index, result.length), 0, { task: ghost.task, done: true });
    }
    return result;
  }

  function renderRow(task: TaskItem, done: boolean, block: BlockKey): ReactNode {
    const stuck = block === 'stuck' && task.stuck !== null;
    const snoozeOpen = snoozeId === task.id;
    const menuOpen = menuId === task.id;
    const highlight = highlightId === task.id;
    const primaryStuck: ReassignMode = task.stuck?.reason === 'inactive' ? 'assign' : 'take_over';
    // „Hängt fest" rows carry the person the task got stuck with (Backoffice 01), not the back office.
    const shown = (stuck ? task.stuck?.previousAssignee : null) ?? task.assignee;
    const rowProps = mobile
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick: () => setDetailId(task.id),
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setDetailId(task.id);
            }
          },
        }
      : {};
    return (
      <div
        key={task.id}
        id={`task-${task.id}`}
        className={clsx(
          styles.row,
          done && styles.rowDone,
          snoozeOpen && styles.rowOpen,
          menuOpen && styles.rowOpen,
          highlight && !done && styles.rowHighlight,
        )}
        {...rowProps}
      >
        <span className={clsx(styles.prio, PRIORITY_CLASS[task.priority])}>
          <span className={styles.prioLetter} aria-label={`Priorität ${task.priority.toUpperCase()}`}>
            {task.priority.toUpperCase()}
          </span>
          {!done && !stuck && !mobile ? (
            <button
              type="button"
              className={styles.check}
              aria-label={`Abgeschlossen: ${task.title}`}
              title="Abgeschlossen"
              disabled={complete.isPending}
              onClick={() => void completeTask(task)}
            >
              <Icon icon={Check} size={16} strokeWidth={2.25} />
            </button>
          ) : null}
        </span>
        <span className={styles.type}>{task.typeLabel}</span>
        <div className={styles.main}>
          <div className={styles.rowTitle}>{task.title}</div>
          <div className={styles.rowReference}>{task.reference}</div>
          {stuck && task.stuck ? <div className={styles.rowWarning}>{task.stuck.label}</div> : null}
        </div>
        {done ? (
          <span className={clsx(styles.due, styles.dueDone)}>erledigt</span>
        ) : (
          <span className={clsx(styles.due, task.due.overdue && styles.dueOverdue)}>{task.due.label}</span>
        )}
        <Avatar
          initials={shown.initials}
          name={shown.name}
          size={26}
          tone="quiet"
          className={styles.avatar}
        />
        {done ? (
          <div className={styles.actionsPlaceholder} />
        ) : (
          <div className={clsx(styles.actions, (snoozeOpen || menuOpen) && styles.actionsVisible)}>
            {stuck ? (
              <>
                <button
                  type="button"
                  className={clsx('pressable', styles.cta)}
                  onClick={() => onStuckAction(task, primaryStuck)}
                >
                  {primaryStuck === 'assign' ? 'Neu zuweisen' : 'Übernehmen'}
                </button>
                <button
                  ref={(element) => {
                    menuTriggers.current.set(task.id, element);
                  }}
                  type="button"
                  className={clsx('pressable', styles.quiet, menuOpen && styles.quietActive)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuId(menuOpen ? null : task.id)}
                >
                  Mehr
                </button>
                <StuckMenu
                  open={menuOpen}
                  task={task}
                  anchorRef={{ current: menuTriggers.current.get(task.id) ?? null }}
                  onClose={() => setMenuId(null)}
                  onSelect={(mode) => onStuckAction(task, mode)}
                />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={clsx('pressable', styles.cta)}
                  onClick={() => openTask(task)}
                >
                  {task.cta}
                </button>
                <button
                  ref={(element) => {
                    snoozeTriggers.current.set(task.id, element);
                  }}
                  type="button"
                  className={clsx('pressable', styles.quiet, snoozeOpen && styles.quietActive)}
                  aria-haspopup="dialog"
                  aria-expanded={snoozeOpen}
                  onClick={() => setSnoozeId(snoozeOpen ? null : task.id)}
                >
                  Später
                </button>
              </>
            )}
          </div>
        )}
        {snoozeOpen ? (
          <SnoozePopover
            quickPicks={quickPicks}
            initialOn={task.due.on}
            initialTime={task.due.time}
            pending={snooze.isPending}
            error={snooze.isError ? errorMessage(snooze.error) : null}
            triggerRef={{ current: snoozeTriggers.current.get(task.id) ?? null }}
            onCancel={() => setSnoozeId(null)}
            onSubmit={(value) => void snoozeTask(task, value).catch(() => undefined)}
          />
        ) : null}
      </div>
    );
  }

  function renderFinancierRow(task: TaskItem): ReactNode {
    return (
      <button
        key={task.id}
        id={`task-${task.id}`}
        type="button"
        className={clsx(styles.row, styles.financierRow, highlightId === task.id && styles.rowHighlight)}
        onClick={() => openTask(task)}
      >
        <span className={clsx(styles.prio, PRIORITY_CLASS[task.priority])}>
          {task.priority.toUpperCase()}
        </span>
        <span className={clsx(styles.type, styles.typeWide)}>{task.typeLabel}</span>
        <span className={styles.main}>
          <span className={styles.rowTitle}>{task.title}</span>
          <span className={styles.rowReference}>{task.reference}</span>
        </span>
        <span className={clsx(styles.due, styles.dueWide, task.due.overdue && styles.dueOverdue)}>
          {task.due.overdue && task.due.on
            ? `überfällig seit ${longDay(task.due.on).slice(0, 6)}`
            : task.due.label}
        </span>
      </button>
    );
  }

  function renderBlock(key: BlockKey, label: string, rows: TaskItem[], note?: string): ReactNode {
    const entries = withGhosts(key, rows);
    if (entries.length === 0) return null;
    return (
      <div key={key}>
        <div className={styles.blockHead}>
          <Eyebrow size={11}>{label}</Eyebrow>
          <span className={styles.blockCount}>{entries.length}</span>
          {note ? <span className={styles.blockNote}>{note}</span> : null}
          {key === 'later' ? (
            <button type="button" className={styles.blockToggle} onClick={() => setLater(false)}>
              Einklappen
            </button>
          ) : null}
        </div>
        <div className={styles.card}>
          {entries.map(({ task, done }) =>
            financier ? renderFinancierRow(task) : renderRow(task, done, key),
          )}
        </div>
      </div>
    );
  }

  const detailTask = useMemo(() => {
    if (!data || detailId === null) return null;
    return [
      ...(data.blocks.stuck ?? []),
      ...data.blocks.overdue,
      ...data.blocks.today,
      ...(data.blocks.later ?? []),
    ].find((entry) => entry.id === detailId);
  }, [data, detailId]);

  // Done rows stay for a second (TQ-01) and keep counting until they leave.
  const ghostRows = ghosts.filter(
    (ghost) =>
      (ghost.block === 'overdue' || ghost.block === 'today') &&
      !data?.blocks[ghost.block].some((row) => row.id === ghost.task.id),
  ).length;
  const headerOpen = (data?.header.open ?? 0) + ghostRows;
  const laterCount = data?.blocks.laterCount ?? 0;
  const empty =
    data !== undefined &&
    (data.blocks.stuck ?? []).length === 0 &&
    data.blocks.overdue.length === 0 &&
    data.blocks.today.length === 0 &&
    ghosts.length === 0;

  return (
    <div className={styles.page}>
      <SetupBanner />
      <div className={styles.header}>
        <h1 className={styles.title}>Meine Tasks</h1>
        <span className={styles.count} aria-label={`${headerOpen} offene Tasks`}>
          {data ? headerOpen : ''}
        </span>
        <span className={styles.headerEnd}>
          {data && !financier ? (
            <span className={styles.doneToday}>{data.header.doneToday} heute erledigt</span>
          ) : null}
          <Button size="large" className={styles.startButton} onClick={() => void navigate('/tasks/queue')}>
            {headerOpen > 0 ? `Queue starten · ${headerOpen}` : 'Queue starten'}
          </Button>
        </span>
      </div>

      <ControllingStrip className={styles.strip} />

      {tasks.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void tasks.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(tasks.error)}
        </Banner>
      ) : null}

      {tasks.isPending ? (
        <div className={styles.blocks} aria-busy="true" aria-label="Wird geladen">
          {[0, 1].map((block) => (
            <div key={block}>
              <div className={styles.blockHead}>
                <Skeleton width={64} height={8} />
              </div>
              <div className={clsx(styles.card, styles.skeletonRows)}>
                {[0, 1, 2].map((row) => (
                  <div key={row} className={styles.skeletonRow}>
                    <Skeleton width={12} />
                    <Skeleton width={72} height={8} />
                    <Skeleton width="42%" />
                    <span style={{ marginLeft: 'auto' }}>
                      <Skeleton width={90} />
                    </span>
                    <Skeleton variant="circle" width={26} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {data ? (
        <div className={styles.blocks}>
          {empty ? (
            financier ? (
              <div className={clsx(styles.card, styles.financierEmpty)}>
                <div className={styles.financierEmptyTitle}>Keine offenen Tasks.</div>
                <div className={styles.financierEmptyText}>
                  Sobald {workspace.approvalRecipientName ?? 'ein Admin'} Ihnen eine Finanzierung zuweist,
                  erscheint sie hier als Task „Neue Finanzierung“.
                </div>
              </div>
            ) : (
              <EmptyState
                variant="centered"
                title="Alles erledigt."
                description={`${doneSentence(data.header)} ${nextDueSentence(data.header, today)}`}
                actions={
                  <>
                    <Button className={styles.emptyPrimary} onClick={() => void navigate('/tasks/queue')}>
                      Queue starten
                    </Button>
                    {laterCount > 0 && !later ? (
                      <Button
                        variant="ghost"
                        className={styles.emptySecondary}
                        onClick={() => setLater(true)}
                      >
                        Später ansehen · {laterCount}
                      </Button>
                    ) : null}
                  </>
                }
              />
            )
          ) : (
            <>
              {data.blocks.stuck && data.blocks.stuck.length > 0
                ? renderBlock(
                    'stuck',
                    'Hängt fest',
                    data.blocks.stuck,
                    'Tasks, die bei anderen nicht vorankommen',
                  )
                : null}
              {renderBlock('overdue', 'Überfällig', data.blocks.overdue)}
              {renderBlock('today', 'Heute', data.blocks.today)}
            </>
          )}

          {later ? (
            <div>
              <div className={styles.blockHead}>
                <Eyebrow size={11}>Später</Eyebrow>
                <span className={styles.blockCount}>{laterCount}</span>
                <button type="button" className={styles.blockToggle} onClick={() => setLater(false)}>
                  Einklappen
                </button>
              </div>
              <div className={styles.card}>
                {(data.blocks.later ?? []).length === 0 ? (
                  <div className={styles.row}>
                    <span className={styles.rowReference}>Keine Tasks mit Termin ab morgen.</span>
                  </div>
                ) : (
                  withGhosts('later', data.blocks.later ?? []).map(({ task, done }) =>
                    financier ? renderFinancierRow(task) : renderRow(task, done, 'later'),
                  )
                )}
              </div>
            </div>
          ) : laterCount > 0 && !empty ? (
            <button type="button" className={styles.laterBar} onClick={() => setLater(true)}>
              <Eyebrow size={11}>Später</Eyebrow>
              <span className={styles.laterText}>
                {laterCount === 1
                  ? '1 Task mit Termin ab morgen'
                  : `${laterCount} Tasks mit Termin ab morgen`}
              </span>
              <span className={styles.laterLink}>Anzeigen</span>
            </button>
          ) : null}

          {financier && !empty ? (
            <div className={styles.financierNote}>
              Jede Task öffnet die zugehörige Finanzierung. Alle zugewiesenen Finanzierungen stehen unter
              Tools › Finanzierungen.
            </div>
          ) : null}
        </div>
      ) : null}

      <NextStepDialog
        open={nextStepTask !== null}
        contactName={nextStepTask?.contact?.name ?? nextStepTask?.reference ?? ''}
        onBack={() => {
          setNextStepTask(null);
          setNextStepError(null);
        }}
        onSubmit={(step) => (nextStepTask ? completeTask(nextStepTask, step) : undefined)}
        pending={complete.isPending}
        error={nextStepError}
      />

      {reassignState ? (
        <ReassignDialog
          open
          mode={reassignState.mode}
          task={reassignState.task}
          assignees={data?.assignees ?? []}
          pending={reassign.isPending}
          error={
            reassign.isError &&
            !(reassign.error instanceof ApiError && Object.keys(reassign.error.fields).length > 0)
              ? reassign.error
              : null
          }
          fieldErrors={reassign.error instanceof ApiError ? reassign.error.fields : {}}
          onClose={() => setReassignState(null)}
          onSubmit={(input) =>
            runReassign(reassignState.task, reassignState.mode, input).catch(() => undefined)
          }
        />
      ) : null}

      <TaskDetailSheet
        taskId={detailId}
        quickPicks={quickPicks}
        onClose={() => setDetailId(null)}
        onOpen={detailTask && !opensDetail(detailTask) ? openTask : null}
        onComplete={(task) => void completeTask(task)}
        onSnooze={snoozeTask}
        completing={complete.isPending}
      />

      <CreateTaskDialog
        open={create}
        onClose={() => void navigate('/tasks')}
        onCreated={(task) => {
          toast({ message: 'Aufgabe angelegt' });
          const goesLater = task.due.on !== null && task.due.on > today;
          void navigate(goesLater ? `/tasks?later=1&task=${task.id}` : `/tasks?task=${task.id}`);
        }}
      />
    </div>
  );
}
